// TradersDiary.js
import React, { useState, useMemo, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import './TradersDiary.css';

const TradersDiary = ({ onBack }) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'June', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [selectedMonth, setSelectedMonth] = useState(0); // Jan (0-indexed)
  const [selectedYear, setSelectedYear] = useState(2026);
  const [tradeData, setTradeData] = useState(() => {
    // Load from localStorage on initial mount
    const saved = localStorage.getItem('tradersDiaryData');
    return saved ? JSON.parse(saved) : {};
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [formData, setFormData] = useState({ net: '', label: '', type: '' });
  const [timeframe, setTimeframe] = useState('1M');

  // Save to localStorage whenever tradeData changes
  useEffect(() => {
    localStorage.setItem('tradersDiaryData', JSON.stringify(tradeData));
  }, [tradeData]);

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  // Convert Sunday=0 to Monday-start week (Mo=0, Tu=1, We=2, Th=3, Fr=4, Sa=5, Su=6)
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Sunday becomes 6, Monday becomes 0
  };

  const handleDayClick = (day) => {
    const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const existingData = tradeData[dateKey];

    setSelectedDay(day);
    setFormData(existingData ? {
      net: Math.abs(existingData.net),
      label: existingData.label,
      type: existingData.net > 0 ? 'profit' : 'loss'
    } : { net: '', label: '', type: '' });
    setShowModal(true);
  };

  const handleSaveTrade = () => {
    if (!formData.net || !formData.type) return;

    const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    const netValue = formData.type === 'profit' ? Number(formData.net) : -Number(formData.net);

    setTradeData(prev => ({
      ...prev,
      [dateKey]: {
        net: netValue,
        label: formData.label || '',
        trades: 1
      }
    }));
    setShowModal(false);
  };

  const handleDeleteTrade = () => {
    const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    const newData = { ...tradeData };
    delete newData[dateKey];
    setTradeData(newData);
    setShowModal(false);
  };

  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];

    // Empty slots for days before the 1st of the month
    for (let i = 0; i < firstDay; i++) {
      days.push({ type: 'empty' });
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const data = tradeData[dateKey];
      days.push({
        type: 'day',
        day,
        data,
        hasProfit: data && data.net > 0,
        hasLoss: data && data.net < 0,
      });
    }

    return days;
  }, [selectedMonth, selectedYear, tradeData]);

  const stats = useMemo(() => {
    const monthData = Object.entries(tradeData).filter(([key]) => {
      const [year, month] = key.split('-').map(Number);
      return year === selectedYear && month === selectedMonth + 1;
    }).map(([, data]) => data);

    const totalPnL = monthData.reduce((sum, d) => sum + d.net, 0);
    const totalTrades = monthData.reduce((sum, d) => sum + (d.trades || 1), 0);
    const wins = monthData.filter(d => d.net > 0);
    const losses = monthData.filter(d => d.net < 0);
    const highestWin = wins.length > 0 ? Math.max(...wins.map(d => d.net)) : 0;
    const highestLoss = losses.length > 0 ? Math.min(...losses.map(d => d.net)) : 0;
    
    const grossProfit = wins.reduce((sum, d) => sum + d.net, 0);
    const grossLoss = losses.reduce((sum, d) => sum + Math.abs(d.net), 0);
    const profitFactor = grossLoss > 0 ? (grossProfit / grossLoss).toFixed(2) : (grossProfit > 0 ? '∞' : '0.00');

    return {
      monthlyPnL: totalPnL,
      totalTrades,
      highestWin,
      highestLoss,
      overallPnL: Object.values(tradeData).reduce((sum, d) => sum + d.net, 0),
      grossProfit,
      grossLoss,
      profitFactor
    };
  }, [selectedMonth, selectedYear, tradeData]);

  // Analytics Chart Data
  const chartData = useMemo(() => {
    const sortedDates = Object.keys(tradeData).sort((a, b) => new Date(a) - new Date(b));
    let cumulative = 0;
    
    // Calculate timeframe cutoff
    let cutoffDate = new Date();
    if (timeframe === '1W') cutoffDate.setDate(cutoffDate.getDate() - 7);
    else if (timeframe === '1M') cutoffDate.setMonth(cutoffDate.getMonth() - 1);
    else if (timeframe === '3M') cutoffDate.setMonth(cutoffDate.getMonth() - 3);
    else if (timeframe === '6M') cutoffDate.setMonth(cutoffDate.getMonth() - 6);
    else if (timeframe === '1Y') cutoffDate.setFullYear(cutoffDate.getFullYear() - 1);
    else cutoffDate = new Date(0); // ALL

    const dataPoints = sortedDates
      .filter(dateStr => new Date(dateStr) >= cutoffDate)
      .map(dateStr => {
        const d = tradeData[dateStr];
        cumulative += d.net;
        return {
          date: dateStr,
          daily: d.net,
          cumulative: cumulative,
          trades: d.trades || 1,
          label: d.label || 'N/A'
        };
      });
      
    // If empty, return a dummy zero line for visual
    if (dataPoints.length === 0) {
      return [{ date: new Date().toISOString().split('T')[0], daily: 0, cumulative: 0 }];
    }
    return dataPoints;
  }, [tradeData, timeframe]);

  const CustomChartTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip-premium">
          <div className="ct-date">{new Date(data.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
          <div className="ct-metric">
            <span>Cumulative:</span>
            <span className={data.cumulative >= 0 ? 'positive' : 'negative'}>
              {data.cumulative >= 0 ? '+' : ''}₹{Math.abs(data.cumulative)}
            </span>
          </div>
          <div className="ct-metric">
            <span>Daily P&L:</span>
            <span className={data.daily >= 0 ? 'positive' : 'negative'}>
              {data.daily >= 0 ? '+' : ''}₹{Math.abs(data.daily)}
            </span>
          </div>
          <div className="ct-sub">Trades: {data.trades} | Setup: {data.label}</div>
        </div>
      );
    }
    return null;
  };

  // Yearly Heatmap Logic
  const yearlyData = useMemo(() => {
    let maxProfit = 0;
    let maxLoss = 0;
    let bestDayKey = null;

    // Find max profit, max loss, and best day
    Object.entries(tradeData).forEach(([key, data]) => {
      if (key.startsWith(selectedYear.toString())) {
        if (data.net > maxProfit) {
          maxProfit = data.net;
          bestDayKey = key;
        }
        if (data.net < 0 && Math.abs(data.net) > maxLoss) {
          maxLoss = Math.abs(data.net);
        }
      }
    });

    const monthsData = months.map((monthName, m) => {
      const daysInMonth = getDaysInMonth(selectedYear, m);
      const firstDay = getFirstDayOfMonth(selectedYear, m);
      const grid = [];
      
      for(let i=0; i<firstDay; i++) {
        grid.push(null);
      }
      
      for(let d=1; d<=daysInMonth; d++) {
        const dateKey = `${selectedYear}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const data = tradeData[dateKey];
        
        let cellClass = 'no-trade';
        if (data) {
          if (data.net > 0) {
            if (dateKey === bestDayKey) cellClass = 'profit-best';
            else if (data.net > maxProfit * 0.66) cellClass = 'profit-high';
            else if (data.net > maxProfit * 0.33) cellClass = 'profit-med';
            else cellClass = 'profit-low';
          } else if (data.net < 0) {
            const absLoss = Math.abs(data.net);
            if (absLoss > maxLoss * 0.66) cellClass = 'loss-high';
            else if (absLoss > maxLoss * 0.33) cellClass = 'loss-med';
            else cellClass = 'loss-low';
          }
        }
        
        grid.push({
          date: d,
          dateKey,
          data,
          cellClass,
          dateStr: `${monthName} ${d}, ${selectedYear}`
        });
      }
      return { monthName, grid };
    });

    return { monthsData, maxProfit, maxLoss };
  }, [selectedYear, tradeData]);

  const [tooltipState, setTooltipState] = useState({ visible: false, x: 0, y: 0, data: null });

  const handleCellMouseEnter = (e, dayObj) => {
    if (!dayObj || !dayObj.data) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipState({
      visible: true,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      data: dayObj
    });
  };

  const handleCellMouseLeave = () => {
    setTooltipState(prev => ({ ...prev, visible: false }));
  };

  const monthTrades = useMemo(() => {
    return Object.entries(tradeData)
      .filter(([key]) => {
        const [year, month] = key.split('-').map(Number);
        return year === selectedYear && month === selectedMonth + 1;
      })
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => b.net - a.net);
  }, [selectedMonth, selectedYear, tradeData]);

  return (
    <div className="traders-diary-page">
      <header className="traders-header">
        <div className="header-left s4u-page-heading">
          <button className="back-btn s4u-back-btn" onClick={onBack}>← Back to Dashboard</button>
          <h1>Traders Diary</h1>
        </div>
        <div className="year-selector">
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027, 2028].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </header>

      <div className="month-tabs">
        {months.map((month, idx) => (
          <button
            className={`month-tab ${idx === selectedMonth ? 'active' : ''}`}
            key={month}
            onClick={() => setSelectedMonth(idx)}
          >
            {month}
          </button>
        ))}
      </div>

      <div className="calendar-section">
        <div className="calendar-grid">
          {calendarDays.map((dayInfo, idx) => (
            <div
              key={idx}
              className={`day-box ${dayInfo.type === 'day' ? (dayInfo.hasProfit ? 'green' : dayInfo.hasLoss ? 'red' : '') : 'empty'}`}
              onClick={() => dayInfo.type === 'day' && handleDayClick(dayInfo.day)}
            >
              {dayInfo.type === 'day' ? (
                <>
                  <div className="day-number">{String(dayInfo.day).padStart(2, '0')}</div>
                  {dayInfo.data && (
                    <>
                      <div className="net">{dayInfo.data.net > 0 ? '+' : ''}{dayInfo.data.net} (Net)</div>
                      <div className="label">{dayInfo.data.label}</div>
                      <div className="plus">{dayInfo.data.net > 0 ? '+' : '-'}</div>
                    </>
                  )}
                </>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <div className="yearly-heatmap-card">
        <div className="heatmap-header">
          <div>
            <h3>YEARLY PERFORMANCE</h3>
            <p className="heatmap-subtitle">Daily Profit & Loss Activity Across All Trading Months</p>
          </div>
          <div className="heatmap-legend">
            <span className="legend-label">Max Loss</span>
            <div className="legend-cells">
              <div className="heatmap-cell legend-cell loss-high"></div>
              <div className="heatmap-cell legend-cell loss-med"></div>
              <div className="heatmap-cell legend-cell loss-low"></div>
            </div>
            <span className="legend-label neutral">Neutral</span>
            <div className="heatmap-cell legend-cell no-trade"></div>
            <div className="legend-cells">
              <div className="heatmap-cell legend-cell profit-low"></div>
              <div className="heatmap-cell legend-cell profit-med"></div>
              <div className="heatmap-cell legend-cell profit-high"></div>
            </div>
            <span className="legend-label">Max Profit</span>
          </div>
        </div>

        <div className="heatmap-months-container">
          {yearlyData.monthsData.map((monthObj, idx) => (
            <div key={idx} className="heatmap-month-block">
              <div className="heatmap-month-name">{monthObj.monthName}</div>
              <div className="heatmap-month-grid">
                {['M','T','W','T','F','S','S'].map((d, i) => (
                  <div key={`header-${i}`} className="heatmap-day-header">{d}</div>
                ))}
                {monthObj.grid.map((dayObj, i) => {
                  if (!dayObj) return <div key={`empty-${i}`} className="heatmap-cell empty"></div>;
                  return (
                    <div
                      key={dayObj.dateKey}
                      className={`heatmap-cell ${dayObj.cellClass}`}
                      onMouseEnter={(e) => handleCellMouseEnter(e, dayObj)}
                      onMouseLeave={handleCellMouseLeave}
                      onClick={() => handleDayClick(dayObj.date)}
                    ></div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="statistics-section">
        <div className="analytics-header">
          <div>
            <h2>PERFORMANCE ANALYTICS</h2>
            <p className="analytics-subtitle">Real-time insights into trading performance and risk behavior</p>
          </div>
          <div className="timeframe-filters">
            {['1W', '1M', '3M', '6M', '1Y', 'ALL'].map(tf => (
              <button 
                key={tf} 
                className={`timeframe-btn ${timeframe === tf ? 'active' : ''}`}
                onClick={() => setTimeframe(tf)}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>

        <div className="analytics-main-graph">
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorPnL" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorLoss" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="date" 
                tick={{fill: '#6b7280', fontSize: 12}} 
                tickFormatter={(val) => new Date(val).toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}
                axisLine={false}
                tickLine={false}
                minTickGap={30}
              />
              <YAxis 
                tick={{fill: '#6b7280', fontSize: 12}} 
                axisLine={false} 
                tickLine={false}
                tickFormatter={(val) => `₹${val}`}
              />
              <RechartsTooltip content={<CustomChartTooltip />} cursor={{ stroke: 'rgba(139, 92, 246, 0.4)', strokeWidth: 1, strokeDasharray: '4 4' }} />
              <ReferenceLine y={0} stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
              <Area 
                type="monotone" 
                dataKey="cumulative" 
                stroke={chartData.length > 0 && chartData[chartData.length-1].cumulative >= 0 ? '#10b981' : '#ef4444'} 
                strokeWidth={3}
                fillOpacity={1} 
                fill={chartData.length > 0 && chartData[chartData.length-1].cumulative >= 0 ? "url(#colorPnL)" : "url(#colorLoss)"}
                activeDot={{ r: 6, fill: '#fff', stroke: '#10b981', strokeWidth: 3, style: { filter: 'drop-shadow(0 0 8px rgba(16,185,129,0.8))' } }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="analytics-cards-grid">
          <div className="premium-metric-card">
            <div className="metric-header">
              <span className="metric-title">Monthly P&L</span>
              {stats.monthlyPnL !== 0 && (
                <span className={`metric-indicator ${stats.monthlyPnL >= 0 ? 'positive' : 'negative'}`}>
                  {stats.monthlyPnL >= 0 ? '↑' : '↓'}
                </span>
              )}
            </div>
            <div className={`metric-value ${stats.monthlyPnL >= 0 ? 'positive' : 'negative'}`}>
              {stats.monthlyPnL >= 0 ? '+' : ''}{stats.monthlyPnL}
            </div>
            <div className="metric-sparkline-placeholder">
              <div className="sparkline-line" style={{ background: stats.monthlyPnL >= 0 ? 'linear-gradient(90deg, transparent, #10b981)' : 'linear-gradient(90deg, transparent, #ef4444)' }}></div>
            </div>
          </div>

          <div className="premium-metric-card">
            <div className="metric-header">
              <span className="metric-title">Total Trades</span>
            </div>
            <div className="metric-value neutral">{stats.totalTrades}</div>
            <div className="metric-sparkline-placeholder">
              <div className="sparkline-dots">
                {[...Array(5)].map((_,i) => <span key={i} className="dot"></span>)}
              </div>
            </div>
          </div>

          <div className="premium-metric-card">
            <div className="metric-header">
              <span className="metric-title">Highest Win</span>
              <span className="metric-indicator positive">↑</span>
            </div>
            <div className="metric-value positive">+{stats.highestWin}</div>
            <div className="metric-sparkline-placeholder">
              <div className="sparkline-bar positive-bar"></div>
            </div>
          </div>

          <div className="premium-metric-card">
            <div className="metric-header">
              <span className="metric-title">Highest Loss</span>
              <span className="metric-indicator negative">↓</span>
            </div>
            <div className="metric-value negative">{stats.highestLoss}</div>
            <div className="metric-sparkline-placeholder">
              <div className="sparkline-bar negative-bar"></div>
            </div>
          </div>

          <div className="premium-metric-card">
            <div className="metric-header">
              <span className="metric-title">Win Rate</span>
            </div>
            <div className="metric-value neutral">
              {stats.totalTrades > 0 ? Math.round((monthTrades.filter(t => t.net > 0).length / stats.totalTrades) * 100) : 0}%
            </div>
            <div className="metric-sparkline-placeholder">
               <div className="progress-track">
                 <div className="progress-fill" style={{width: `${stats.totalTrades > 0 ? Math.round((monthTrades.filter(t => t.net > 0).length / stats.totalTrades) * 100) : 0}%`}}></div>
               </div>
            </div>
          </div>

          <div className="premium-metric-card">
            <div className="metric-header">
              <span className="metric-title">Profit Factor</span>
            </div>
            <div className={`metric-value ${stats.profitFactor >= 1 ? 'positive' : 'negative'}`}>
              {stats.profitFactor}
            </div>
            <div className="metric-sparkline-placeholder">
              <div className="ratio-visual">
                <div className="ratio-win" style={{flex: stats.grossProfit}}></div>
                <div className="ratio-loss" style={{flex: stats.grossLoss}}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="trades-section">
        <h2>Trades</h2>
        <div className="trades-box">
          <div className="trade-header">
            <button className="active">Top Winning Trades</button>
            <button>Top Losing Trades</button>
          </div>
          <div className="trade-list">
            {monthTrades.length === 0 ? (
              <div className="trade-item">No trades recorded for this month</div>
            ) : (
              monthTrades.map((trade) => (
                <div className="trade-item" key={trade.date}>
                  <span> {trade.label} <small>({trade.date})</small></span>
                  <span className={`change ${trade.net > 0 ? 'positive' : 'negative'}`}>
                    {trade.net > 0 ? '+' : ''}{trade.net}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div className="trade-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="trade-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add Trade - {months[selectedMonth]} {selectedDay}, {selectedYear}</h3>
            
            <div className="trade-type-selector">
              <button
                className={`profit-btn ${formData.type === 'profit' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, type: 'profit' })}
              >
                + Profit
              </button>
              <button
                className={`loss-btn ${formData.type === 'loss' ? 'active' : ''}`}
                onClick={() => setFormData({ ...formData, type: 'loss' })}
              >
                - Loss
              </button>
            </div>

            <div className="form-group">
              <label>Amount</label>
              <input
                type="number"
                value={formData.net}
                onChange={(e) => setFormData({ ...formData, net: e.target.value })}
                placeholder="Enter amount"
              />
            </div>

            <div className="form-group">
              <label>Label/Note (Setup)</label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Breakout, Stop Loss"
              />
            </div>

            <div className="modal-actions">
              <button className="save-btn" onClick={handleSaveTrade}>Save</button>
              {tradeData[`${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`] && (
                <button className="delete-btn" onClick={handleDeleteTrade}>Delete</button>
              )}
              <button className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Hover Tooltip Portal Simulation */}
      {tooltipState.visible && tooltipState.data && tooltipState.data.data && (
        <div
          className="heatmap-hover-tooltip"
          style={{
            left: `${tooltipState.x}px`,
            top: `${tooltipState.y}px`
          }}
        >
          <div className="tooltip-date">{tooltipState.data.dateStr}</div>
          <div className={`tooltip-pnl ${tooltipState.data.data.net > 0 ? 'positive' : 'negative'}`}>
            Net P&L: {tooltipState.data.data.net > 0 ? '+' : ''}₹{Math.abs(tooltipState.data.data.net)}
          </div>
          <div className="tooltip-detail">Trades: {tooltipState.data.data.trades || 1}</div>
          <div className="tooltip-detail">Win Rate: {tooltipState.data.data.net > 0 ? '100%' : '0%'}</div>
          <div className="tooltip-detail">Setup: {tooltipState.data.data.label || 'N/A'}</div>
        </div>
      )}
    </div>
  );
};

export default TradersDiary;